<template>
  <t-form-group
    :label="formLabel"
    :description="description"
    class="fluency-bid-adjustment-input"
  >
    <p-input-group>
      <p-select
        v-if="prepend.length > 1"
        v-model="increaseOrDecrease"
        :options="prepend"
        :disabled="disabled"
        @update:modelValue="updateValue(percentageValue, true)"
        @blur="evaluateFocus"
      />
      <p-input-group-addon v-else>
        {{ prepend[0].text }}
      </p-input-group-addon>

      <p-input-text
        ref="input"
        v-model="percentageValue"
        :autofocus="autofocus"
        :placeholder="placeholder"
        :disabled="increaseOrDecrease === 0 || disabled"
        :class="{ shrinky: shrinky }"
        @update:modelValue="updateValue($event)"
        @keyup.enter="evaluateFocus"
        @blur="evaluateFocus"
      />
      <p-input-group-addon> % </p-input-group-addon>
    </p-input-group>
  </t-form-group>
</template>

<script>
import debounce from 'lodash-es/debounce'
export default {
  name: 'bidAdjustmentInput',
  props: {
    modelValue: {
      type: [Number, String]
    },
    prepend: {
      type: Array,
      default: () => {
        return [
          { text: 'Increase by', value: 1 },
          { text: 'Decrease by', value: -1 }
        ]
      }
    },
    description: String,
    adjustmentType: String,
    hideLabel: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Value as a %'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    shrinky: {
      type: Boolean,
      default: false
    },
    allowNull: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      percentageValue: this.allowNull ? null : 0,
      increaseOrDecrease: 1,
      nextFocusTarget: null
    }
  },
  computed: {
    formLabel () {
      return this.hideLabel ? '' : 'Bid Adjustment'
    },
    increaseOnly () {
      return this.prepend.length === 1 && this.prepend[0].value === 1
    }
  },
  watch: {
    modelValue: {
      handler: function (newVal, oldVal) {
        if (newVal === 0 || !newVal) {
          this.percentageValue = this.allowNull ? null : 0
          if (this.prepend.some((v) => v.text === 'Exclude' && v.value === 0)) {
            this.$setCompat(this, 'increaseOrDecrease', 0)
          }
        } else if (this.increaseOrDecrease !== 0) {
          this.percentageValue = (newVal - 1).toPrecision(4) * 100
          if (this.percentageValue < 0 && !this.increaseOnly) {
            this.percentageValue = Math.abs(this.percentageValue)
            this.$setCompat(this, 'increaseOrDecrease', -1)
          }
        }
      },
      immediate: true
    },
    prepend: {
      handler: function (newVal, oldVal) {
        // Check to see if there is an `Exclude` option set.
        // If there is AND this.modelValue is zero, we will preselect that option.
        // This will disable the numeric input until the user changes the dropdown.
        if (this.modelValue === 0 || !this.modelValue) {
          this.percentageValue = this.allowNull ? null : 0
          if (newVal.some((v) => v.text === 'Exclude' && v.value === 0)) {
            this.$setCompat(this, 'increaseOrDecrease', 0)
          }
        }
      }
    }
  },
  methods: {
    updateValue (newVal, blurAfter = false) {
      if (this.increaseOrDecrease === 0) {
        this.$emit('update:modelValue', 0)
        return
      }
      if (newVal !== null) {
        let modifiedValue = Math.abs(newVal / 100)
        if (this.increaseOrDecrease > 0) {
          modifiedValue = 1 + modifiedValue
        } else if (this.increaseOrDecrease < 0) {
          if (modifiedValue > 0.9) {
            // making floor for bid modifier -90% -SNG
            modifiedValue = 1 - 0.9
          } else {
            modifiedValue = 1 - modifiedValue
          }
        }
        this.$emit('update:modelValue', modifiedValue)
      }

      if (blurAfter && newVal !== null) {
        this.$emit('blur')
      }
    },
    captureNextFocusTarget (e) {
      this.nextFocusTarget = e.target
    },

    captureNextFocusTargetFromKeyboard (e) {
      const focused = document.activeElement
      if (!focused.closest('.fluency-bid-adjustment-input')) {
        return
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        this.nextFocusTarget = null
      }
      this.evaluateFocus()
    },
    evaluateFocus: debounce(function () {
      // I needed a blur event to come out of this component when focus leaves the input field AND the select field
      // but not when switching between the two. - JC
      // Same - AP
      if (this.nextFocusTarget?.closest('.fluency-bid-adjustment-input')) {
        return
      }
      this.$emit('blur')
    }, 100)
  },
  mounted () {
    if (this.autofocus) {
      const input = this.$refs?.input?.$el
      this.nextFocusTarget = input
      input?.focus()
    }

    document.addEventListener('mousedown', this.captureNextFocusTarget)
    document.addEventListener('keydown', this.captureNextFocusTargetFromKeyboard)
  },
  beforeUnmount () {
    document.removeEventListener('mousedown', this.captureNextFocusTarget)
    document.removeEventListener('keydown', this.captureNextFocusTargetFromKeyboard)
  }
}
</script>

<style scoped>
.shrinky {
  width: 60px;
}
</style>
